import {
    productEvent,
    type ProductJourney
} from "@shared/analytics/productEvents";
import { BuyWidget } from "@solomon-apps/shared";
import { ArrowShortLeftIcon } from "@solomon-labs/design/components/icons";
import { Button } from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import React from "react";

import { TokenSelector } from "@/pages/USDv/components/TokenSelector";

/**
 * Right-column swap-USDv surface. Hoisted to the top of the page's
 * action-card ternary so it overrides every other state (steady
 * card, applying, morphing, preview, etc). All "Swap USDv" buttons
 * across the page flip `isBuyingUSDv` true and land here.
 */
export function BuyUsdvPanel({
    analytics,
    isWalletConnected,
    onBack,
    onSwapComplete
}: {
    analytics?: ProductJourney;
    /** Lets enrolled users (who have USDv to sell) flip the swap
     *  direction. Disconnected preview keeps the rate row's flip
     *  button disabled (nothing to sell yet). */
    isWalletConnected: boolean;
    onBack: () => void;
    /** Runs 3s after a swap confirms. Leaving this panel falls back
     *  through the page's own ternary, so an earning wallet lands on
     *  Positions and a wallet holding USDv but not yet enrolled lands
     *  on the earn projection — no routing decision needed here. */
    onSwapComplete: () => void;
}): React.ReactElement {
    return (
        <motion.div
            key="buy-usdv"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{
                opacity: { duration: 0.3, ease: "easeOut" },
                y: { duration: 0.3, ease: "easeOut" }
            }}
            className="flex flex-col gap-3"
        >
            <Button
                variant="link"
                size="sm"
                onClick={() => {
                    if (analytics) analytics.close("back");
                    else
                        productEvent("swap:form_closed", {
                            entry_point: "rewards_entry",
                            close_method: "back"
                        });
                    onBack();
                }}
                className="self-start"
            >
                <ArrowShortLeftIcon className="size-4" />
                Back
            </Button>
            <BuyWidget
                analytics={analytics}
                analyticsEntryPoint="rewards_buy_panel"
                canFlip={isWalletConnected}
                TokenSelectorComponent={TokenSelector}
                onSuccessReturn={onSwapComplete}
            />
        </motion.div>
    );
}
